import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    AppBar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Toolbar,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    BarChart3,
    Check,
    ClipboardCopy,
    Clock3,
    Landmark,
    LogOut,
    Moon,
    Plus,
    Sun,
    Tag,
    TrendingUp,
    Wallet,
    X,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { getMe } from '../../services/authService';
import { epLogout } from '../../services/epAuthService';
import {
    cancelWithdrawal,
    createPartnerOnboardingLink,
    createWithdrawalRequest,
    EPWithdrawalRequest,
    generateReferralCode,
    getCodeDetails,
    getMyCodes,
    getPartnerPayoutStatus,
    getMyWithdrawals,
    PayoutMethod,
    PartnerPayoutStatus,
    ReferralCodeDetail,
    ReferralCodeSummary,
    WithdrawalStatus,
    WithdrawalSummary,
} from '../../services/epService';
import { useColorMode } from '../../context/ColorModeContext';

interface WithdrawalFormState {
    amount: string;
    payoutMethod: PayoutMethod;
    upiId: string;
    accountHolderName: string;
    accountNumber: string;
    ifscCode: string;
    note: string;
}

const defaultWithdrawalForm: WithdrawalFormState = {
    amount: '',
    payoutMethod: 'bank_transfer',
    upiId: '',
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
    note: '',
};

const EPDashboard: React.FC = () => {
    const navigate = useNavigate();
    const { mode, toggleMode } = useColorMode();
    const [userName, setUserName] = useState<string>('');
    const [codes, setCodes] = useState<ReferralCodeSummary[]>([]);
    const [totalEarned, setTotalEarned] = useState<number>(0);
    const [loadingCodes, setLoadingCodes] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [copiedCode, setCopiedCode] = useState<string | null>(null);

    const [withdrawalSummary, setWithdrawalSummary] = useState<WithdrawalSummary | null>(null);
    const [withdrawals, setWithdrawals] = useState<EPWithdrawalRequest[]>([]);
    const [loadingWithdrawals, setLoadingWithdrawals] = useState(true);
    const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
    const [withdrawalForm, setWithdrawalForm] = useState<WithdrawalFormState>(defaultWithdrawalForm);
    const [requestingWithdrawal, setRequestingWithdrawal] = useState(false);
    const [cancellingWithdrawalId, setCancellingWithdrawalId] = useState<string | null>(null);
    const [payoutStatus, setPayoutStatus] = useState<PartnerPayoutStatus | null>(null);
    const [loadingPayoutStatus, setLoadingPayoutStatus] = useState(true);
    const [creatingOnboardingLink, setCreatingOnboardingLink] = useState(false);

    // Detail dialog
    const [selectedCode, setSelectedCode] = useState<ReferralCodeDetail | null>(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [loadingDetail, setLoadingDetail] = useState(false);

    const fetchCodes = useCallback(async () => {
        setLoadingCodes(true);
        try {
            const data = await getMyCodes();
            setCodes(data.codes);
            setTotalEarned(data.summary.totalEarned);
        } catch (err) {
            console.error('Error fetching codes:', err);
        } finally {
            setLoadingCodes(false);
        }
    }, []);

    const fetchWithdrawals = useCallback(async () => {
        setLoadingWithdrawals(true);
        try {
            const data = await getMyWithdrawals();
            setWithdrawals(data.requests);
            setWithdrawalSummary(data.summary);
        } catch (err) {
            console.error('Error fetching withdrawals:', err);
            toast.error('Failed to load withdrawal details');
        } finally {
            setLoadingWithdrawals(false);
        }
    }, []);

    const fetchPayoutStatus = useCallback(async () => {
        setLoadingPayoutStatus(true);
        try {
            const status = await getPartnerPayoutStatus();
            setPayoutStatus(status);
        } catch (err) {
            console.error('Error fetching payout status:', err);
        } finally {
            setLoadingPayoutStatus(false);
        }
    }, []);

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const userData = await getMe();
                if (userData) {
                    const name = userData.name ?? userData.user?.name ?? '';
                    setUserName(name);
                }
            } catch (err) {
                console.error('Error fetching user data:', err);
            }
        };
        fetchUser();
        fetchCodes();
        fetchWithdrawals();
        fetchPayoutStatus();
    }, [fetchCodes, fetchWithdrawals, fetchPayoutStatus]);

    const handleLogout = () => {
        epLogout();
        navigate('/login');
    };

    const handleGenerate = async () => {
        setGenerating(true);
        try {
            const res = await generateReferralCode();
            toast.success(`Code generated: ${res.referralCode.code}`);
            await fetchCodes();
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Failed to generate code');
        } finally {
            setGenerating(false);
        }
    };

    const handleCopy = (code: string) => {
        navigator.clipboard.writeText(code);
        setCopiedCode(code);
        setTimeout(() => setCopiedCode(null), 2000);
    };

    const handleOpenDetail = async (code: string) => {
        setDetailOpen(true);
        setLoadingDetail(true);
        try {
            const detail = await getCodeDetails(code);
            setSelectedCode(detail);
        } catch (err: any) {
            toast.error('Failed to load code details');
            setDetailOpen(false);
        } finally {
            setLoadingDetail(false);
        }
    };

    const handleOpenWithdrawalDialog = () => {
        setWithdrawalForm(defaultWithdrawalForm);
        setWithdrawDialogOpen(true);
    };

    const handleCompleteStripeSetup = async () => {
        setCreatingOnboardingLink(true);
        try {
            const res = await createPartnerOnboardingLink();
            if (res?.url) {
                window.open(res.url, '_blank', 'noopener,noreferrer');
                toast.info('Stripe onboarding opened in a new tab');
            }
            await fetchPayoutStatus();
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Failed to start Stripe onboarding');
        } finally {
            setCreatingOnboardingLink(false);
        }
    };

    const handleSubmitWithdrawal = async () => {
        if (!payoutStatus?.payoutsEnabled) {
            toast.error('Please complete Stripe payout setup first');
            return;
        }

        const amountNum = Number(withdrawalForm.amount);
        if (!Number.isFinite(amountNum) || amountNum <= 0) {
            toast.error('Enter a valid withdrawal amount');
            return;
        }

        if (withdrawalSummary) {
            if (amountNum < withdrawalSummary.minWithdrawalAmount) {
                toast.error(`Minimum withdrawal amount is ₹${withdrawalSummary.minWithdrawalAmount}`);
                return;
            }
            if (amountNum > withdrawalSummary.availableBalance) {
                toast.error('Amount exceeds available balance');
                return;
            }
        }

        let payoutDetails: {
            upiId?: string;
            accountHolderName?: string;
            accountNumber?: string;
            ifscCode?: string;
        } = {};

        if (withdrawalForm.payoutMethod === 'upi') {
            const upiId = withdrawalForm.upiId.trim();
            const upiRegex = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/;
            if (!upiRegex.test(upiId)) {
                toast.error('Please enter a valid UPI ID (example: name@bank)');
                return;
            }
            payoutDetails = { upiId };
        } else {
            const accountHolderName = withdrawalForm.accountHolderName.trim();
            const accountNumber = withdrawalForm.accountNumber.trim();
            const ifscCode = withdrawalForm.ifscCode.trim().toUpperCase();
            const accountNumberRegex = /^\d{8,20}$/;
            const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;

            if (accountHolderName.length < 2) {
                toast.error('Enter account holder name');
                return;
            }
            if (!accountNumberRegex.test(accountNumber)) {
                toast.error('Account number must be 8 to 20 digits');
                return;
            }
            if (!ifscRegex.test(ifscCode)) {
                toast.error('Enter a valid IFSC code');
                return;
            }
            payoutDetails = {
                accountHolderName,
                accountNumber,
                ifscCode,
            };
        }

        setRequestingWithdrawal(true);
        try {
            const res = await createWithdrawalRequest({
                amount: Number(amountNum.toFixed(2)),
                payoutMethod: withdrawalForm.payoutMethod,
                payoutDetails,
                note: withdrawalForm.note.trim() || undefined,
            });
            toast.success(res?.message || 'Withdrawal completed');
            setWithdrawDialogOpen(false);
            setWithdrawalForm(defaultWithdrawalForm);
            await fetchWithdrawals();
            await fetchPayoutStatus();
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Failed to process withdrawal');
        } finally {
            setRequestingWithdrawal(false);
        }
    };

    const handleCancelWithdrawal = async (requestId: string) => {
        setCancellingWithdrawalId(requestId);
        try {
            await cancelWithdrawal(requestId);
            toast.success('Withdrawal request cancelled');
            await fetchWithdrawals();
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Failed to cancel withdrawal request');
        } finally {
            setCancellingWithdrawalId(null);
        }
    };

    const getStatusColor = (
        status: WithdrawalStatus
    ): 'default' | 'success' | 'warning' | 'error' => {
        if (status === 'approved') return 'success';
        if (status === 'pending') return 'warning';
        if (status === 'rejected') return 'error';
        return 'default';
    };

    const formatAmount = (amount: number) =>
        amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const formatDate = (dateStr: string) =>
        new Date(dateStr).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });

    const formatDateTime = (dateStr?: string) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const payoutLabel = (request: EPWithdrawalRequest): string => {
        if (request.payoutMethod === 'upi') {
            return request.payoutDetails.upiId ? `UPI • ${request.payoutDetails.upiId}` : 'UPI';
        }
        const account = request.payoutDetails.accountNumber || '';
        const last4 = account.length >= 4 ? account.slice(-4) : account;
        const ifsc = request.payoutDetails.ifscCode || '';
        return `Bank • ****${last4}${ifsc ? ` (${ifsc})` : ''}`;
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
            <AppBar
                position="static"
                sx={{
                    background:
                        mode === 'dark'
                            ? 'linear-gradient(135deg, #0b1220 0%, #5a4bfc 65%, #00b894 120%)'
                            : 'linear-gradient(135deg, #5a4bfc 0%, #00b894 100%)',
                    boxShadow: mode === 'dark' ? '0 14px 45px rgba(0,0,0,0.45)' : '0 2px 8px rgba(0,0,0,0.1)',
                }}
            >
                <Toolbar>
                    <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
                        Zaptick AI – Partner Portal
                    </Typography>
                    <Tooltip title={mode === 'dark' ? 'Light mode' : 'Dark mode'}>
                        <IconButton
                            onClick={toggleMode}
                            color="inherit"
                            sx={{
                                mr: 1,
                                borderRadius: 999,
                                border: (theme) => `1px solid ${alpha(theme.palette.common.white, 0.25)}`,
                            }}
                            aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                        >
                            {mode === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
                        </IconButton>
                    </Tooltip>
                    <Button
                        color="inherit"
                        startIcon={<LogOut size={20} />}
                        onClick={handleLogout}
                        sx={{ textTransform: 'none' }}
                    >
                        Logout
                    </Button>
                </Toolbar>
            </AppBar>

            <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
                <Stack spacing={4}>
                    <Box>
                        <Typography variant="h4" fontWeight={700} gutterBottom>
                            Welcome back, {userName || 'Partner'}! 👋
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Generate referral codes and earn commission on every subscription.
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                            gap: 3,
                        }}
                    >
                        <Card sx={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                            <CardContent>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 2,
                                            bgcolor: 'primary.main',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Tag size={24} color="white" />
                                    </Box>
                                    <Box>
                                        <Typography variant="h4" fontWeight={700}>
                                            {codes.length}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Total Codes
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>

                        <Card sx={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                            <CardContent>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 2,
                                            bgcolor: 'success.main',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <BarChart3 size={24} color="white" />
                                    </Box>
                                    <Box>
                                        <Typography variant="h4" fontWeight={700}>
                                            {codes.reduce((s, c) => s + c.usageCount, 0)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Total Subscriptions
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>

                        <Card
                            sx={{
                                boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                                background: 'linear-gradient(135deg, #5a4bfc 0%, #00b894 100%)',
                                color: 'white',
                            }}
                        >
                            <CardContent>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 2,
                                            bgcolor: 'rgba(255,255,255,0.2)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <TrendingUp size={24} color="white" />
                                    </Box>
                                    <Box>
                                        <Typography variant="h4" fontWeight={700}>
                                            ₹{formatAmount(totalEarned)}
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.85 }}>
                                            Total Earned
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Box>

                    <Card sx={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                justifyContent="space-between"
                                alignItems={{ xs: 'flex-start', md: 'center' }}
                                spacing={2}
                                mb={3}
                            >
                                <Box>
                                    <Typography variant="h6" fontWeight={700}>
                                        Withdraw Earnings
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Direct payout works only after Stripe Connect setup is complete.
                                    </Typography>
                                </Box>
                                <Button
                                    variant="contained"
                                    startIcon={<Wallet size={18} />}
                                    onClick={handleOpenWithdrawalDialog}
                                    disabled={
                                        loadingPayoutStatus ||
                                        !payoutStatus?.payoutsEnabled ||
                                        !withdrawalSummary ||
                                        withdrawalSummary.availableBalance < withdrawalSummary.minWithdrawalAmount
                                    }
                                    sx={{
                                        background: 'linear-gradient(135deg, #5a4bfc 0%, #00b894 100%)',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        px: 3,
                                    }}
                                >
                                    Withdraw Now
                                </Button>
                            </Stack>

                            {!loadingPayoutStatus && payoutStatus && !payoutStatus.payoutsEnabled && (
                                <Alert
                                    severity="warning"
                                    action={
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={handleCompleteStripeSetup}
                                            disabled={creatingOnboardingLink}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            {creatingOnboardingLink ? 'Opening...' : 'Complete Stripe Setup'}
                                        </Button>
                                    }
                                    sx={{ mb: 2 }}
                                >
                                    Stripe payout setup incomplete.
                                    {payoutStatus.currentlyDueCount > 0
                                        ? ` ${payoutStatus.currentlyDueCount} verification field(s) pending.`
                                        : ' Finish onboarding to enable direct withdrawals.'}
                                </Alert>
                            )}

                            {loadingWithdrawals || !withdrawalSummary ? (
                                <Box display="flex" justifyContent="center" py={4}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <Stack spacing={3}>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                                            gap: 2,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                bgcolor: 'rgba(0,184,148,0.08)',
                                            }}
                                        >
                                            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                                                <Wallet size={16} />
                                                <Typography variant="caption" color="text.secondary">
                                                    Available Balance
                                                </Typography>
                                            </Stack>
                                            <Typography variant="h5" fontWeight={700} color="success.main">
                                                ₹{formatAmount(withdrawalSummary.availableBalance)}
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                bgcolor: 'rgba(255,193,7,0.08)',
                                            }}
                                        >
                                            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                                                <Clock3 size={16} />
                                                <Typography variant="caption" color="text.secondary">
                                                    In Process
                                                </Typography>
                                            </Stack>
                                            <Typography variant="h5" fontWeight={700} color="warning.main">
                                                ₹{formatAmount(withdrawalSummary.pendingAmount)}
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                bgcolor: 'rgba(90,75,252,0.08)',
                                            }}
                                        >
                                            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                                                <Landmark size={16} />
                                                <Typography variant="caption" color="text.secondary">
                                                    Total Paid Out
                                                </Typography>
                                            </Stack>
                                            <Typography variant="h5" fontWeight={700} color="primary.main">
                                                ₹{formatAmount(withdrawalSummary.totalWithdrawn)}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Typography variant="caption" color="text.secondary">
                                        Minimum withdrawal amount: ₹{formatAmount(withdrawalSummary.minWithdrawalAmount)}
                                    </Typography>

                                    <Divider />

                                    <Typography variant="subtitle1" fontWeight={700}>
                                        Withdrawal History
                                    </Typography>

                                    {withdrawals.length === 0 ? (
                                        <Box textAlign="center" py={3}>
                                            <Typography variant="body2" color="text.secondary">
                                                No withdrawal requests yet.
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={{ overflowX: 'auto' }}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 700 }}>Requested On</TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }}>Payout</TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }}>Note</TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {withdrawals.map((request) => (
                                                        <TableRow key={request.id} hover>
                                                            <TableCell>{formatDateTime(request.createdAt)}</TableCell>
                                                            <TableCell>
                                                                <Typography fontWeight={700}>
                                                                    ₹{formatAmount(request.amount)}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>{payoutLabel(request)}</TableCell>
                                                            <TableCell>
                                                                <Stack spacing={0.5}>
                                                                    <Chip
                                                                        label={request.status.toUpperCase()}
                                                                        size="small"
                                                                        color={getStatusColor(request.status)}
                                                                        sx={{ width: 'fit-content' }}
                                                                    />
                                                                    {request.status !== 'pending' && (
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {formatDateTime(request.processedAt)}
                                                                        </Typography>
                                                                    )}
                                                                </Stack>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {request.rejectionReason || request.note || '—'}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                {request.status === 'pending' ? (
                                                                    <Button
                                                                        size="small"
                                                                        color="error"
                                                                        onClick={() => handleCancelWithdrawal(request.id)}
                                                                        disabled={cancellingWithdrawalId === request.id}
                                                                        sx={{ textTransform: 'none' }}
                                                                    >
                                                                        {cancellingWithdrawalId === request.id
                                                                            ? 'Cancelling...'
                                                                            : 'Cancel'}
                                                                    </Button>
                                                                ) : (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        —
                                                                    </Typography>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </Box>
                                    )}
                                </Stack>
                            )}
                        </CardContent>
                    </Card>

                    <Card sx={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                justifyContent="space-between"
                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                                spacing={2}
                                mb={3}
                            >
                                <Box>
                                    <Typography variant="h6" fontWeight={700}>
                                        My Referral Codes
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Click a code to see detailed earnings
                                    </Typography>
                                </Box>
                                <Button
                                    variant="contained"
                                    startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <Plus size={18} />}
                                    onClick={handleGenerate}
                                    disabled={generating}
                                    sx={{
                                        background: 'linear-gradient(135deg, #5a4bfc 0%, #00b894 100%)',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        px: 3,
                                    }}
                                >
                                    {generating ? 'Generating…' : 'Generate New Code'}
                                </Button>
                            </Stack>

                            {loadingCodes ? (
                                <Box display="flex" justifyContent="center" py={6}>
                                    <CircularProgress />
                                </Box>
                            ) : codes.length === 0 ? (
                                <Box
                                    sx={{
                                        textAlign: 'center',
                                        py: 6,
                                        borderRadius: 2,
                                        border: '2px dashed',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Tag size={40} color="#bbb" />
                                    <Typography variant="body1" color="text.secondary" mt={2}>
                                        No referral codes yet. Generate your first code!
                                    </Typography>
                                </Box>
                            ) : (
                                <Stack spacing={2}>
                                    {codes.map((c) => (
                                        <Box
                                            key={c.id}
                                            onClick={() => handleOpenDetail(c.code)}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                flexWrap: 'wrap',
                                                gap: 2,
                                                px: 3,
                                                py: 2,
                                                borderRadius: 2,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    borderColor: 'primary.main',
                                                    boxShadow: '0 2px 12px rgba(90,75,252,0.12)',
                                                    bgcolor: 'rgba(90,75,252,0.03)',
                                                },
                                            }}
                                        >
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                <Chip
                                                    label={c.code}
                                                    sx={{
                                                        fontFamily: 'monospace',
                                                        fontWeight: 700,
                                                        fontSize: '0.9rem',
                                                        bgcolor: 'rgba(90,75,252,0.1)',
                                                        color: 'primary.main',
                                                    }}
                                                />
                                                <Tooltip title={copiedCode === c.code ? 'Copied!' : 'Copy code'}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleCopy(c.code);
                                                        }}
                                                    >
                                                        {copiedCode === c.code ? (
                                                            <Check size={16} color="#00b894" />
                                                        ) : (
                                                            <ClipboardCopy size={16} />
                                                        )}
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>

                                            <Stack direction="row" spacing={4} alignItems="center">
                                                <Box textAlign="center">
                                                    <Typography variant="h6" fontWeight={700}>
                                                        {c.usageCount}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Uses
                                                    </Typography>
                                                </Box>
                                                <Box textAlign="center">
                                                    <Typography
                                                        variant="h6"
                                                        fontWeight={700}
                                                        color={c.totalEarned > 0 ? 'success.main' : 'text.primary'}
                                                    >
                                                        ₹{formatAmount(c.totalEarned)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Earned
                                                    </Typography>
                                                </Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatDate(c.createdAt)}
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    ))}
                                </Stack>
                            )}
                        </CardContent>
                    </Card>
                </Stack>
            </Container>

            <Dialog
                open={withdrawDialogOpen}
                onClose={() => setWithdrawDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="h6" fontWeight={700}>
                                Withdraw Now
                            </Typography>
                            {withdrawalSummary && (
                                <Typography variant="caption" color="text.secondary">
                                    Available: ₹{formatAmount(withdrawalSummary.availableBalance)}
                                </Typography>
                            )}
                        </Box>
                        <IconButton onClick={() => setWithdrawDialogOpen(false)}>
                            <X size={20} />
                        </IconButton>
                    </Stack>
                </DialogTitle>

                <DialogContent sx={{ pt: 1 }}>
                    <Stack spacing={2}>
                        <TextField
                            label="Amount (₹)"
                            type="number"
                            value={withdrawalForm.amount}
                            onChange={(e) => setWithdrawalForm((prev) => ({ ...prev, amount: e.target.value }))}
                            inputProps={{ min: 0, step: 0.01 }}
                            fullWidth
                        />

                        <TextField
                            select
                            label="Payout Method"
                            value={withdrawalForm.payoutMethod}
                            onChange={(e) =>
                                setWithdrawalForm((prev) => ({
                                    ...prev,
                                    payoutMethod: e.target.value as PayoutMethod,
                                }))
                            }
                            fullWidth
                        >
                            <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                        </TextField>

                        {withdrawalForm.payoutMethod === 'upi' ? (
                            <TextField
                                label="UPI ID"
                                placeholder="name@bank"
                                value={withdrawalForm.upiId}
                                onChange={(e) => setWithdrawalForm((prev) => ({ ...prev, upiId: e.target.value }))}
                                fullWidth
                            />
                        ) : (
                            <Stack spacing={2}>
                                <TextField
                                    label="Account Holder Name"
                                    value={withdrawalForm.accountHolderName}
                                    onChange={(e) =>
                                        setWithdrawalForm((prev) => ({
                                            ...prev,
                                            accountHolderName: e.target.value,
                                        }))
                                    }
                                    fullWidth
                                />
                                <TextField
                                    label="Account Number"
                                    value={withdrawalForm.accountNumber}
                                    onChange={(e) =>
                                        setWithdrawalForm((prev) => ({
                                            ...prev,
                                            accountNumber: e.target.value.replace(/\D/g, ''),
                                        }))
                                    }
                                    fullWidth
                                />
                                <TextField
                                    label="IFSC Code"
                                    value={withdrawalForm.ifscCode}
                                    onChange={(e) =>
                                        setWithdrawalForm((prev) => ({
                                            ...prev,
                                            ifscCode: e.target.value.toUpperCase(),
                                        }))
                                    }
                                    fullWidth
                                />
                            </Stack>
                        )}

                        <TextField
                            label="Note (optional)"
                            value={withdrawalForm.note}
                            onChange={(e) => setWithdrawalForm((prev) => ({ ...prev, note: e.target.value }))}
                            fullWidth
                            multiline
                            minRows={3}
                        />
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={() => setWithdrawDialogOpen(false)} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmitWithdrawal}
                        disabled={requestingWithdrawal || !payoutStatus?.payoutsEnabled}
                        sx={{
                            textTransform: 'none',
                            background: 'linear-gradient(135deg, #5a4bfc 0%, #00b894 100%)',
                        }}
                    >
                        {requestingWithdrawal ? 'Processing...' : 'Withdraw Now'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={detailOpen}
                onClose={() => setDetailOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="h6" fontWeight={700}>
                                Code Details
                            </Typography>
                            {selectedCode && (
                                <Chip
                                    label={selectedCode.code}
                                    size="small"
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontWeight: 700,
                                        bgcolor: 'rgba(90,75,252,0.1)',
                                        color: 'primary.main',
                                        mt: 0.5,
                                    }}
                                />
                            )}
                        </Box>
                        <IconButton onClick={() => setDetailOpen(false)}>
                            <X size={20} />
                        </IconButton>
                    </Stack>
                </DialogTitle>

                <DialogContent>
                    {loadingDetail ? (
                        <Box display="flex" justifyContent="center" py={6}>
                            <CircularProgress />
                        </Box>
                    ) : selectedCode ? (
                        <Stack spacing={3}>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                                    gap: 2,
                                    p: 2,
                                    borderRadius: 2,
                                    bgcolor: 'rgba(90,75,252,0.04)',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <Box textAlign="center">
                                    <Typography variant="h5" fontWeight={700} color="primary">
                                        {selectedCode.usageCount}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Total Uses
                                    </Typography>
                                </Box>
                                <Box textAlign="center">
                                    <Typography variant="h5" fontWeight={700} color="success.main">
                                        ₹{formatAmount(selectedCode.totalEarned)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Total Earned
                                    </Typography>
                                </Box>
                                <Box textAlign="center">
                                    <Typography variant="h5" fontWeight={700}>
                                        {formatDate(selectedCode.createdAt)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Created On
                                    </Typography>
                                </Box>
                            </Box>

                            <Divider />

                            <Typography variant="subtitle1" fontWeight={700}>
                                Subscription History
                            </Typography>

                            {selectedCode.usages.length === 0 ? (
                                <Box textAlign="center" py={4}>
                                    <Typography variant="body2" color="text.secondary">
                                        No subscriptions via this code yet.
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ overflowX: 'auto' }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                    Plan Amount
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, color: 'success.main' }}>
                                                    Your Earnings
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {selectedCode.usages.map((usage, idx) => (
                                                <TableRow key={idx} hover>
                                                    <TableCell>
                                                        {usage.user ? (
                                                            <Box>
                                                                <Typography variant="body2" fontWeight={600}>
                                                                    {usage.user.name}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {usage.user.email}
                                                                </Typography>
                                                            </Box>
                                                        ) : (
                                                            <Typography variant="caption" color="text.secondary">
                                                                —
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={usage.planName}
                                                            size="small"
                                                            color="primary"
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        ₹{formatAmount(usage.subscriptionAmount)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography fontWeight={700} color="success.main">
                                                            ₹{formatAmount(usage.commission)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>{formatDate(usage.date)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            )}
                        </Stack>
                    ) : null}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default EPDashboard;
